<template>
  <div class="bg-background-light text-slate-800 antialiased h-screen flex overflow-hidden">
    <AppSidebar currentPage="announcements" />

    <main class="flex-1 flex flex-col h-full overflow-hidden">
      <!-- Header -->
      <header class="h-16 bg-surface border-b border-slate-200 flex items-center justify-between px-4 md:px-8 flex-shrink-0">
        <div>
          <h2 class="text-lg font-bold text-slate-900">Announcements & Broadcasts</h2>
          <p class="text-xs text-slate-500">Create, manage and track communications to your client base.</p>
        </div>
      </header>

      <div class="flex-1 overflow-y-auto p-8">
        <div class="max-w-[1400px] mx-auto">
          <div class="grid grid-cols-1 lg:grid-cols-5 gap-8">

            <!-- ===== COMPOSE PANEL ===== -->
            <div class="lg:col-span-2 space-y-6">
              <div class="bg-surface rounded-xl border border-slate-200 shadow-sm p-6">
                <h3 class="text-base font-semibold text-slate-800 mb-5 flex items-center gap-2">
                  <span class="material-symbols-outlined text-primary" style="font-size: 20px;">edit_note</span>
                  Compose Announcement
                </h3>
                <div class="space-y-4">

                  <!-- Subject -->
                  <div>
                    <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Subject</label>
                    <input v-model="form.subject" type="text" placeholder="Announcement title..."
                      class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
                  </div>

                  <!-- Message -->
                  <div>
                    <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Message</label>
                    <textarea v-model="form.message" rows="5" placeholder="Write your announcement here..."
                      class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"></textarea>
                  </div>

                  <!-- Recipients Mode Toggle -->
                  <div>
                    <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Recipients</label>
                    <div class="flex items-center gap-1 bg-slate-100 p-1 rounded-lg mb-3">
                      <button
                        @click="form.recipientMode = 'group'"
                        class="flex-1 py-1.5 rounded-md text-sm font-medium transition-colors"
                        :class="form.recipientMode === 'group' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
                        By Group
                      </button>
                      <button
                        @click="form.recipientMode = 'specific'; loadClientsForPicker()"
                        class="flex-1 py-1.5 rounded-md text-sm font-medium transition-colors"
                        :class="form.recipientMode === 'specific' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
                        Specific Clients
                      </button>
                    </div>

                    <!-- Group dropdown -->
                    <select v-if="form.recipientMode === 'group'"
                      v-model="form.recipientType"
                      class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                      <option value="All Clients">All Clients</option>
                      <option value="LLC">LLC Clients</option>
                      <option value="Individual">Individual Clients</option>
                      <option value="C-Corp">C-Corp Clients</option>
                      <option value="S-Corp">S-Corp Clients</option>
                    </select>

                    <!-- Specific client picker -->
                    <div v-else>
                      <!-- Search -->
                      <div class="relative mb-2">
                        <span class="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400" style="font-size: 16px;">search</span>
                        <input
                          v-model="clientSearch"
                          type="text"
                          placeholder="Search clients..."
                          class="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                      </div>

                      <!-- Loading clients -->
                      <div v-if="loadingClients" class="flex justify-center py-4">
                        <svg class="animate-spin h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>

                      <!-- Client list -->
                      <div v-else class="border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                        <!-- Select All row -->
                        <div
                          @click="toggleSelectAll"
                          class="flex items-center gap-3 px-3 py-2.5 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                          <div class="h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                            :class="allSelected ? 'bg-primary border-primary' : 'border-slate-300'">
                            <span v-if="allSelected" class="material-symbols-outlined text-white" style="font-size: 12px;">check</span>
                          </div>
                          <span class="text-xs font-semibold text-slate-600">Select All ({{ filteredClients.length }})</span>
                        </div>

                        <div v-if="filteredClients.length === 0" class="px-3 py-4 text-center text-sm text-slate-400">
                          No clients found
                        </div>

                        <div
                          v-for="client in filteredClients"
                          :key="client.Id"
                          @click="toggleClient(client)"
                          class="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0">
                          <div class="h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                            :class="isClientSelected(client) ? 'bg-primary border-primary' : 'border-slate-300'">
                            <span v-if="isClientSelected(client)" class="material-symbols-outlined text-white" style="font-size: 12px;">check</span>
                          </div>
                          <div class="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                            {{ getInitials(client.Title) }}
                          </div>
                          <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-slate-800 truncate">{{ client.Title }}</p>
                            <p class="text-xs text-slate-400 truncate">{{ client.ClientEmail || client.EntityType || '' }}</p>
                          </div>
                        </div>
                      </div>

                      <!-- Selected count badge -->
                      <div v-if="form.selectedClients.length > 0"
                        class="mt-2 flex items-center gap-2 flex-wrap">
                        <span class="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                          {{ form.selectedClients.length }} client(s) selected
                        </span>
                        <button @click="form.selectedClients = []" class="text-xs text-slate-400 hover:text-red-500 transition-colors">
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Attachment -->
                  <div>
                    <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Attachment (optional)</label>
                    <div
                      class="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors"
                      :class="form.attachmentFile ? 'border-primary/50 bg-primary/5' : 'border-slate-200 hover:border-primary/30'"
                      @click="$refs.attachInput.click()"
                      @dragover.prevent
                      @drop.prevent="onAttachDrop">
                      <input ref="attachInput" type="file" class="hidden" @change="onAttachChange" />
                      <div v-if="!form.attachmentFile" class="flex flex-col items-center gap-1">
                        <span class="material-symbols-outlined text-slate-400" style="font-size: 28px;">attach_file</span>
                        <p class="text-xs text-slate-500">Click or drag to attach a file</p>
                      </div>
                      <div v-else class="flex items-center gap-3">
                        <span class="material-symbols-outlined text-primary" style="font-size: 24px;">description</span>
                        <div class="flex-1 text-left min-w-0">
                          <p class="text-sm font-semibold text-slate-800 truncate">{{ form.attachmentFile.name }}</p>
                          <p class="text-xs text-slate-400">{{ formatFileSize(form.attachmentFile.size) }}</p>
                        </div>
                        <button @click.stop="form.attachmentFile = null"
                          class="text-slate-400 hover:text-red-500 transition-colors">
                          <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Send via Email toggle -->
                  <div class="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <p class="text-sm font-medium text-slate-700">Send via Email</p>
                      <p class="text-xs text-slate-400">Send email to clients via Microsoft 365</p>
                    </div>
                    <button @click="form.sendEmail = !form.sendEmail"
                      class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                      :class="form.sendEmail ? 'bg-primary' : 'bg-slate-300'">
                      <span class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow"
                        :class="form.sendEmail ? 'translate-x-6' : 'translate-x-1'"></span>
                    </button>
                  </div>

                  <!-- Error / Success -->
                  <div v-if="formError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
                    <span class="material-symbols-outlined" style="font-size: 16px;">error</span>
                    {{ formError }}
                  </div>
                  <div v-if="formSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600 flex items-center gap-2">
                    <span class="material-symbols-outlined" style="font-size: 16px;">check_circle</span>
                    {{ formSuccess }}
                  </div>

                  <!-- Send button -->
                  <button @click="sendAnnouncement" :disabled="sending"
                    class="w-full py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover shadow-md shadow-primary/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                    <span v-if="!sending" class="material-symbols-outlined" style="font-size: 18px;">send</span>
                    <svg v-else class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ sending ? 'Sending...' : 'Send Announcement' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- ===== HISTORY PANEL ===== -->
            <div class="lg:col-span-3">
              <div class="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 class="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <span class="material-symbols-outlined text-primary" style="font-size: 20px;">history</span>
                    History
                  </h3>
                  <span class="text-xs text-slate-500">{{ announcements.length }} announcements</span>
                </div>

                <!-- Loading -->
                <div v-if="loading" class="flex items-center justify-center py-16">
                  <svg class="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>

                <!-- Empty -->
                <div v-else-if="announcements.length === 0" class="text-center py-16">
                  <span class="material-symbols-outlined text-slate-300" style="font-size: 48px;">campaign</span>
                  <p class="text-slate-400 text-sm mt-2">No announcements yet</p>
                </div>

                <!-- List -->
                <div v-else class="divide-y divide-slate-100">
                  <div v-for="ann in announcements" :key="ann.id"
                    class="p-5 hover:bg-slate-50/50 transition-colors group">
                    <div class="flex items-start justify-between gap-4">
                      <div class="flex-1 min-w-0">
                        <!-- Status badge + date -->
                        <div class="flex items-center gap-2 mb-1">
                          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border"
                            :class="ann.statusClass">
                            <span class="material-symbols-outlined" style="font-size: 12px;">{{ ann.statusIcon }}</span>
                            {{ ann.statusLabel }}
                          </span>
                          <span class="text-xs text-slate-400">{{ ann.date }}</span>
                        </div>

                        <!-- Subject -->
                        <h4 class="text-sm font-semibold text-slate-900 truncate">{{ ann.title }}</h4>

                        <!-- Message preview -->
                        <p class="text-xs text-slate-500 mt-1 line-clamp-2">{{ ann.message }}</p>

                        <!-- Meta row -->
                        <div class="flex items-center gap-3 mt-2 flex-wrap">
                          <span class="flex items-center gap-1 text-xs text-slate-400">
                            <span class="material-symbols-outlined" style="font-size: 14px;">group</span>
                            {{ ann.recipientDisplay }}
                          </span>
                          <span v-if="ann.sentCount" class="flex items-center gap-1 text-xs text-slate-400">
                            <span class="material-symbols-outlined" style="font-size: 14px;">visibility</span>
                            {{ ann.sentCount }} sent
                          </span>
                          <span class="flex items-center gap-1 text-xs text-slate-400">
                            <span class="material-symbols-outlined" style="font-size: 14px;">person</span>
                            {{ ann.authorName }}
                          </span>
                          <!-- Attachment indicator -->
                          <a v-if="ann.attachmentUrl" :href="ann.attachmentUrl" target="_blank"
                            class="flex items-center gap-1 text-xs text-primary hover:underline">
                            <span class="material-symbols-outlined" style="font-size: 14px;">attach_file</span>
                            {{ ann.attachmentName || 'Attachment' }}
                          </a>
                        </div>
                      </div>

                      <!-- Delete button -->
                      <button
                        @click="deleteAnnouncement(ann)"
                        class="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 flex-shrink-0"
                        title="Delete">
                        <span class="material-symbols-outlined" style="font-size: 18px;">delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import AppSidebar from '../components/AppSidebar.vue';

export default {
  name: 'AdminAnnouncements',
  components: { AppSidebar },

  data() {
    return {
      loading: true,
      sending: false,
      loadingClients: false,
      formError: '',
      formSuccess: '',
      clientSearch: '',
      allClients: [],

      form: {
        subject: '',
        message: '',
        recipientMode: 'group',
        recipientType: 'All Clients',
        selectedClients: [],
        sendEmail: false,
        attachmentFile: null
      },

      announcements: []
    };
  },

  computed: {
    filteredClients() {
      if (!this.clientSearch.trim()) return this.allClients;
      const q = this.clientSearch.toLowerCase();
      return this.allClients.filter(c =>
        (c.Title || '').toLowerCase().includes(q) ||
        (c.ClientEmail || '').toLowerCase().includes(q)
      );
    },

    allSelected() {
      return this.filteredClients.length > 0 &&
        this.filteredClients.every(c => this.isClientSelected(c));
    }
  },

  async mounted() {
    await this.loadAnnouncements();
  },

  methods: {
    // ===== LOAD ANNOUNCEMENTS =====
    async loadAnnouncements() {
      try {
        this.loading = true;
        const results = await this.$sharepoint.getAnnouncements();
        this.announcements = results.map(item => ({
          id: item.Id,
          title: item.Subject || item.Title || 'No Subject',
          message: item.DisplayMessage || item.MessageContent || '',
          attachmentUrl: item.AttachmentUrl || null,
          attachmentName: item.AttachmentName || null,
          recipientType: item.RecipientType || 'All Clients',
          recipientDisplay: item.RecipientDisplay || item.RecipientType || 'All Clients',
          sentCount: item.SentCount || 0,
          authorName: item.AuthorName || 'Admin',
          date: this.formatDate(item.Created),
          statusLabel: item.SentStatus === 'Sent' ? 'Sent' : 'Draft',
          statusClass: item.SentStatus === 'Sent'
            ? 'bg-green-50 text-green-700 border-green-100'
            : 'bg-slate-50 text-slate-600 border-slate-200',
          statusIcon: item.SentStatus === 'Sent' ? 'check_circle' : 'edit'
        }));
      } catch (error) {
        console.error('Error loading announcements:', error);
      } finally {
        this.loading = false;
      }
    },

    // ===== LOAD CLIENTS FOR PICKER =====
    async loadClientsForPicker() {
      if (this.allClients.length > 0) return; // already loaded
      this.loadingClients = true;
      try {
        this.allClients = await this.$sharepoint.getClients();
      } catch (error) {
        console.error('Error loading clients for picker:', error);
        this.allClients = [];
      } finally {
        this.loadingClients = false;
      }
    },

    // ===== CLIENT SELECTION HELPERS =====
    isClientSelected(client) {
      return this.form.selectedClients.some(c => c.Id === client.Id);
    },

    toggleClient(client) {
      if (this.isClientSelected(client)) {
        this.form.selectedClients = this.form.selectedClients.filter(c => c.Id !== client.Id);
      } else {
        this.form.selectedClients.push(client);
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        // Deselect all filtered
        const filteredIds = new Set(this.filteredClients.map(c => c.Id));
        this.form.selectedClients = this.form.selectedClients.filter(c => !filteredIds.has(c.Id));
      } else {
        // Add all filtered that aren't already selected
        const existing = new Set(this.form.selectedClients.map(c => c.Id));
        this.filteredClients.forEach(c => {
          if (!existing.has(c.Id)) this.form.selectedClients.push(c);
        });
      }
    },

    getInitials(name) {
      if (!name) return '??';
      const parts = name.trim().split(' ');
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },

    // ===== SEND ANNOUNCEMENT =====
    async sendAnnouncement() {
      this.formError = '';
      this.formSuccess = '';

      if (!this.form.subject.trim()) {
        this.formError = 'Please enter a subject.';
        return;
      }
      if (!this.form.message.trim()) {
        this.formError = 'Please enter a message.';
        return;
      }
      if (this.form.recipientMode === 'specific' && this.form.selectedClients.length === 0) {
        this.formError = 'Please select at least one client.';
        return;
      }

      this.sending = true;
      try {
        const user = this.$sharepoint.getCurrentUser();
        const isSpecific = this.form.recipientMode === 'specific';
        const selectedNames = isSpecific
          ? this.form.selectedClients.map(c => c.Title)
          : [];

        await this.$sharepoint.sendBroadcastMessage({
          subject: this.form.subject,
          message: this.form.message,
          recipientType: isSpecific ? 'All' : this.form.recipientType,
          selectedClientNames: selectedNames,
          sendEmail: this.form.sendEmail,
          sender: user?.name || user?.username || 'Admin',
          attachmentFile: this.form.attachmentFile,
          recipients: isSpecific
            ? this.form.selectedClients
            : [{ id: 'group', name: this.form.recipientType }]
        });

        const recipientLabel = isSpecific
          ? `${selectedNames.length} client(s)`
          : this.form.recipientType;

        this.formSuccess = `✅ Announcement sent to ${recipientLabel}!`;

        // Reset form
        this.form = {
          subject: '',
          message: '',
          recipientMode: 'group',
          recipientType: 'All Clients',
          selectedClients: [],
          sendEmail: false,
          attachmentFile: null
        };
        this.clientSearch = '';

        await this.loadAnnouncements();
      } catch (error) {
        this.formError = 'Failed to send. Please try again.';
        console.error(error);
      } finally {
        this.sending = false;
      }
    },

    // ===== DELETE ANNOUNCEMENT =====
    async deleteAnnouncement(ann) {
      if (!confirm(`Delete announcement "${ann.title}"?`)) return;
      try {
        await this.$sharepoint.deleteAnnouncement(ann.id);
        this.announcements = this.announcements.filter(a => a.id !== ann.id);
      } catch (error) {
        console.error('Failed to delete announcement:', error);
        alert('Failed to delete. Please try again.');
      }
    },

    // ===== ATTACHMENT HELPERS =====
    onAttachChange(e) {
      const file = e.target.files[0];
      if (file) this.form.attachmentFile = file;
    },

    onAttachDrop(e) {
      const file = e.dataTransfer.files[0];
      if (file) this.form.attachmentFile = file;
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1048576).toFixed(1) + ' MB';
    },

    // ===== HELPERS =====
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffHrs = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffHrs < 1) return 'Just now';
      if (diffHrs < 24) return `${diffHrs}h ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString();
    }
  }
};
</script>